import { API_BASE_URL } from '@synergy/config';
import { Address, NotificationOrigin, PaymentMethod } from '@synergy/models';
import { BankIcon } from '@synergy/theme';
import { Box, TextField, Theme, Tooltip } from '@mui/material';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormattedMessage } from 'react-intl';
import { PaloField } from '@synergy/components-palo';
import { WithTheme as WithThemeProps, withTheme } from '@mui/styles';
import { getAddressDisplayValue } from '@synergy/utils';

import { AccountApi } from '../../../../../common/api/account-api';
import { Contact } from '../../../../../common/models/contact';
import { Customer } from '../../../../../common/models/customer';
import { PaloCustomerQuickSearch } from '../../../../../common/components/palo/palo-customer-quick-search';
import { PaloNotificationOverrideHint } from '../../../../../common/components/palo/palo-notification-override-hint';
import { PaloOrderCustomerLock } from '../../../../../common/components/palo/palo-locked-badge';
import {
    PartialAccountContact,
    PoolAccountContactAutocompleteList,
} from '../../../../../common/components/autocomplete-list/account-contact-autocomplete-list/pool-account-contact-autocomplete-list';
import {
    PartialAddress,
    PoolAccountAddressAutocompleteList,
} from '../../../../../common/components/autocomplete-list/account-address-autocomplete-list/pool-account-address-autocomplete-list';
import { PoolCustomerAutocompleteList } from '../../../../../common/components/autocomplete-list/customer-autocomplete-list/pool-customer-autocomplete-list';
import {
    PurchaseOrderDetailOverviewFormValues,
    purchaseOrderDetailOverviewFieldNames as fieldNames,
} from '../models/purchase-order-detail-form-values';
import { fetchInvoiceAddresses, onlyOne } from '../../../../../common/utils/only-one';

interface PaloOverviewSectionCustomerProps extends WithThemeProps<Theme> {
    readonly?: boolean;
}

class PaloOverviewSectionCustomerBase extends FormComponent<
    PurchaseOrderDetailOverviewFormValues,
    PaloOverviewSectionCustomerProps
> {
    private api = new AccountApi(API_BASE_URL);

    /** The customer whose lists are currently being read. */
    private lastCustomerId?: number;

    private handleCustomerSelected =
        (formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>) =>
        (customer: Customer | null, contact?: Contact, address?: Address) => {
            const deliveryAddress = address?.isDeliveryAddress ? address : undefined;
            const invoiceAddress = address?.isInvoiceAddress ? address : undefined;
            const customerContact = contact ?? customer?.mainContact;

            formikBag.setFieldValue(fieldNames.customerAccount, customer);
            formikBag.setFieldValue(fieldNames.customerContact, customerContact ?? null);
            formikBag.setFieldValue(fieldNames.customerAvisoContact, customerContact ?? null);
            formikBag.setFieldValue(fieldNames.deliveryAddress, deliveryAddress ?? null);
            formikBag.setFieldValue(fieldNames.invoiceAddress, invoiceAddress ?? null);

            formikBag.setFieldValue(fieldNames.classifications, customer?.classifications ?? []);

            const costCentre = deliveryAddress?.costCentre ?? invoiceAddress?.costCentre;
            if (costCentre) {
                formikBag.setFieldValue(fieldNames.costCentre, costCentre);
            }
            formikBag.setFieldValue(fieldNames.functionalAsset, null);
            this.applyInvoiceAddress(formikBag, invoiceAddress);

            void this.fillWhatIsUnambiguous(formikBag, customer, {
                contact: customerContact,
                deliveryAddress,
                invoiceAddress,
            });
        };

    /**
     * Fills the fields the customer leaves no choice about.
     *
     * Three required fields stood empty although the customer had exactly one
     * contact, one delivery and one invoice address — the invoice does this for
     * its invoice address and was the better example (#2210, R-14). The lists
     * are not part of the customer the search hands over, so they are read the
     * way the fields' own dropdowns read them.
     */
    private fillWhatIsUnambiguous = async (
        formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>,
        customer: Customer | null,
        filled: { contact?: Contact; deliveryAddress?: Address; invoiceAddress?: Address }
    ) => {
        this.lastCustomerId = customer?.id;

        if (!customer?.id) {
            return;
        }

        const [deliveries, onlyInvoice, contacts] = await Promise.all([
            filled.deliveryAddress ? undefined : this.api.getAddresses(customer.id, undefined, false),
            filled.invoiceAddress ? undefined : fetchInvoiceAddresses(this.api, customer.id).then(onlyOne),
            filled.contact ? undefined : this.api.getContacts(customer.id),
        ]);

        // The customer may have been changed again while this was on the way.
        // Asking the form would not tell: the bag carries the values of the
        // render this call was made in, not the current ones.
        if (this.lastCustomerId !== customer.id) {
            return;
        }

        // The main contact first, then the one contact there is: a member with
        // four people left the required field empty although one of them is
        // the main contact (#2239 R-138).
        const contact = contacts?.success
            ? contacts.result.find(candidate => candidate.isMainContact) ?? onlyOne(contacts.result)
            : undefined;
        if (contact) {
            formikBag.setFieldValue(fieldNames.customerContact, contact);
            formikBag.setFieldValue(fieldNames.customerAvisoContact, contact);
        }

        const onlyDelivery = deliveries?.success
            ? onlyOne(deliveries.result.filter(candidate => candidate.isDeliveryAddress))
            : undefined;
        if (onlyDelivery) {
            formikBag.setFieldValue(fieldNames.deliveryAddress, onlyDelivery);
        }

        if (onlyInvoice) {
            formikBag.setFieldValue(fieldNames.invoiceAddress, onlyInvoice);
            this.applyInvoiceAddress(formikBag, onlyInvoice);
        }

        const costCentre = onlyDelivery?.costCentre ?? onlyInvoice?.costCentre;
        if (costCentre) {
            formikBag.setFieldValue(fieldNames.costCentre, costCentre);
        }
    };

    /** The charging address follows the invoice address, unless the supplier bills. */
    private applyInvoiceAddress = (
        formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>,
        invoiceAddress?: Address
    ) => {
        if (!invoiceAddress) {
            return;
        }

        const supplier = formikBag.values.supplierAccount;
        if (supplier === undefined || !supplier.isChargingSupplier) {
            formikBag.setFieldValue(fieldNames.chargingInvoiceAddress, invoiceAddress);
        }
    };

    private handleInvoiceAddressSelected =
        (formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>) => (address?: Address | null) => {
            formikBag.setFieldValue(fieldNames.invoiceAddress, address);

            if (address && address.isInvoiceAddress) {
                const supplier = formikBag.values.supplierAccount;
                if (supplier === undefined || !supplier?.isChargingSupplier) {
                    formikBag.setFieldValue(fieldNames.chargingInvoiceAddress, address);
                }
            }
        };

    private handleDeliveryAddressSelected =
        (formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>) => (address: Address | null) => {
            formikBag.setFieldValue(fieldNames.deliveryAddress, address);
            if (address && address.costCentre) {
                formikBag.setFieldValue(fieldNames.costCentre, address.costCentre);
            }
        };

    private getCurrentAccountId(formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>) {
        const customerAccount = formikBag.values.customerAccount;
        return customerAccount && customerAccount.id;
    }

    private getAdornment = (formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>) => {
        const recipient = formikBag.values.customerAccount;

        return [
            recipient?.paymentMethod === PaymentMethod.Debit ? (
                <Tooltip key={1} title={<FormattedMessage id="app.invoices.debit" />} placement="top-end">
                    <Box sx={{ height: '20px', mr: 1 }}>
                        <BankIcon sx={{ fontSize: 20 }} htmlColor={this.props.theme.palette.secondary.main} />
                    </Box>
                </Tooltip>
            ) : null,
            <PaloNotificationOverrideHint
                key={2}
                accountId={recipient?.id}
                origins={[NotificationOrigin.PurchaseOrderManual, NotificationOrigin.PurchaseOrderShop]}
            />,
        ];
    };

    private renderTextControl(
        form: FormikProps<PurchaseOrderDetailOverviewFormValues>,
        field: FieldProps<string>['field'],
        label: React.ReactNode,
        area: string
    ) {
        return (
            <PaloField
                label={label}
                name={field.name}
                area={area}
                error={this.hasError(form, field.name) ? this.getError(form, field.name) : undefined}
            >
                <TextField
                    {...field}
                    value={field.value ?? ''}
                    size="small"
                    fullWidth
                    disabled={!form.values.customerAccount || this.props.readonly}
                    error={this.hasError(form, field.name)}
                />
            </PaloField>
        );
    }

    public render() {
        const { readonly } = this.props;

        return (
            <>
                <Field name={fieldNames.customerAccount}>
                    {({ field, form }: FieldProps<Customer>) => (
                        <PaloField
                            label={
                                <>
                                    <FormattedMessage id="app.label.customer" /> *
                                </>
                            }
                            name={field.name}
                            badge={<PaloOrderCustomerLock />}
                            error={this.hasError(form, field.name) ? this.getError(form, field.name) : undefined}
                            area="customer"
                        >
                            <PaloCustomerQuickSearch
                                id="purchase-order-quick-search-customer"
                                picked={!!form.values.customerAccount}
                                readonly={readonly}
                                onSelected={this.handleCustomerSelected(form)}
                            >
                                {searchButton => (
                                    <PoolCustomerAutocompleteList
                                        label={<></>}
                                        required={false}
                                        onSelected={this.handleCustomerSelected(form)}
                                        initialValue={''}
                                        error={this.hasError(form, field.name)}
                                        readOnly={readonly}
                                        {...field}
                                        adornment={[...this.getAdornment(form), searchButton]}
                                        isLocked={false}
                                    />
                                )}
                            </PaloCustomerQuickSearch>
                        </PaloField>
                    )}
                </Field>
                <Field name={fieldNames.customerContact}>
                    {({ field, form }: FieldProps<PartialAccountContact>) => (
                        <PaloField
                            label={
                                <>
                                    <FormattedMessage id="app.label.customerContact" /> *
                                </>
                            }
                            name={field.name}
                            area="customerContact"
                            error={this.hasError(form, field.name) ? this.getError(form, field.name) : undefined}
                        >
                            <PoolAccountContactAutocompleteList
                                accountId={this.getCurrentAccountId(form)}
                                label={<></>}
                                required={false}
                                disabled={!form.values.customerAccount}
                                onSelected={this.setFieldValue(form, field.name)}
                                error={this.hasError(form, field.name)}
                                readOnly={readonly}
                                {...field}
                            />
                        </PaloField>
                    )}
                </Field>
                <Field name={fieldNames.customerAvisoContact}>
                    {({ field, form }: FieldProps<PartialAccountContact>) => (
                        <PaloField
                            label={<FormattedMessage id="app.label.avisoContact" />}
                            name={field.name}
                            area="avisoContact"
                            error={this.hasError(form, field.name) ? this.getError(form, field.name) : undefined}
                        >
                            <PoolAccountContactAutocompleteList
                                accountId={this.getCurrentAccountId(form)}
                                label={<></>}
                                disabled={!form.values.customerAccount}
                                onSelected={this.setFieldValue(form, field.name)}
                                error={this.hasError(form, field.name)}
                                readOnly={readonly}
                                {...field}
                            />
                        </PaloField>
                    )}
                </Field>
                <Field name={fieldNames.deliveryAddress}>
                    {({ field, form }: FieldProps<PartialAddress>) => (
                        <PaloField
                            label={
                                <>
                                    <FormattedMessage id="app.label.deliveryAddress" /> *
                                </>
                            }
                            name={field.name}
                            area="deliveryAddress"
                            error={this.hasError(form, field.name) ? this.getError(form, field.name) : undefined}
                        >
                            <PoolAccountAddressAutocompleteList
                                accountId={this.getCurrentAccountId(form)}
                                autocompleteType="DeliveryAddress"
                                label={<></>}
                                required={false}
                                disabled={!form.values.customerAccount}
                                onSelected={this.handleDeliveryAddressSelected(form)}
                                error={this.hasError(form, field.name)}
                                readOnly={readonly}
                                {...field}
                            />
                        </PaloField>
                    )}
                </Field>
                <Field name={fieldNames.invoiceAddress}>
                    {({ field, form }: FieldProps<PartialAddress>) => (
                        <PaloField
                            label={
                                <>
                                    <FormattedMessage id="app.label.invoiceAddress" /> *
                                </>
                            }
                            name={field.name}
                            area="invoiceAddress"
                            error={this.hasError(form, field.name) ? this.getError(form, field.name) : undefined}
                        >
                            <PoolAccountAddressAutocompleteList
                                accountId={this.getCurrentAccountId(form)}
                                autocompleteType="InvoiceAddress"
                                label={<></>}
                                disabled={!form.values.customerAccount}
                                onSelected={this.handleInvoiceAddressSelected(form)}
                                error={this.hasError(form, field.name)}
                                readOnly={readonly}
                                required={false}
                                {...field}
                            />
                        </PaloField>
                    )}
                </Field>
                <Field name={fieldNames.chargingInvoiceAddress}>
                    {({ field }: FieldProps<Address>) => (
                        <PaloField
                            label={<FormattedMessage id="app.label.chargingInvoiceAddress" />}
                            area="chargingInvoiceAddress"
                        >
                            <TextField
                                {...field}
                                size="small"
                                fullWidth
                                disabled
                                value={field.value ? getAddressDisplayValue(field.value) : ''}
                            />
                        </PaloField>
                    )}
                </Field>
                <Field name={fieldNames.costCentre}>
                    {({ field, form }: FieldProps<string>) =>
                        this.renderTextControl(
                            form,
                            field,
                            <FormattedMessage id="app.label.costCentre" />,
                            'costCentre'
                        )
                    }
                </Field>
                <Field name={fieldNames.orderReference}>
                    {({ field, form }: FieldProps<string>) =>
                        this.renderTextControl(
                            form,
                            field,
                            <FormattedMessage id="app.label.orderReference" />,
                            'orderReference'
                        )
                    }
                </Field>
                <Field name={fieldNames.supplierOfferNumber}>
                    {({ field, form }: FieldProps<string>) =>
                        this.renderTextControl(
                            form,
                            field,
                            <FormattedMessage id="app.label.offerNumber" />,
                            'offerNumber'
                        )
                    }
                </Field>
            </>
        );
    }
}

export const PaloOverviewSectionCustomer = withTheme<Theme, typeof PaloOverviewSectionCustomerBase>(
    PaloOverviewSectionCustomerBase
);
